import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@chakra-ui/react';
import React from 'react';
import { AlertIcon, Paragraph, TextLink } from 'ui-components';
import { SOCIAL_PLATFORMS } from '~/constants';
import { SupportedPlatforms } from '~/screens/CreatorAudienceDemographics';

const metricsInfo = {
  instagram: [
    {
      title: 'Average Reach',
      description:
        'Average number of unique accounts that have seen the posts and stories.',
    },
    {
      title: 'Average Views',
      description:
        'Average number of times the Talent’s posts and stories have been seen.',
    },
    {
      title: 'Average Engagements',
      description:
        'Average number of likes, comments, saves, and shares on each post.',
    },
    {
      title: 'Follower Engagement Rate',
      description:
        'Percentage of followers who have interacted with the post—out of the total follower count.',
    },
    {
      title: 'Reach Engagement Rate',
      description:
        'Average percentage of unique accounts who have seen and engaged with the posts.',
    },
    {
      title: 'View Engagement Rate',
      description:
        'Average percentage of engagements to total views of the reels.',
    },
    {
      title: 'Average Reels Views',
      description: 'Average number of video sessions on the reels.',
    },
    {
      title: 'Average Story Reach',
      description:
        'Average number of unique accounts that have seen the Tallent’s stories.',
    },
    {
      title: 'Average Story Views ',
      description:
        'Average number of times the Talent’s stories have been seen.',
    },
  ],
  tiktok: [
    {
      title: 'Average Views',
      description:
        "Average number of times each of the Talent's videos has been viewed.",
    },
    {
      title: 'Average Engagements',
      description: 'Average number of likes, comments and shares.',
    },
    {
      title: 'Average Likes',
      description: 'Average number of likes on the Talent’s posts.',
    },
    {
      title: 'Average Comments',
      description: 'Average number of comments on the Talent’s posts.',
    },
    {
      title: 'Average Shares',
      description: 'Average number of shares of the Talent’s posts.',
    },
    {
      title: 'View Engagement Rate',
      description: 'Average engagements divided by the average video views.',
    },
  ],
  youtube: [
    {
      title: 'Average Views',
      description:
        "Average number of times each of the Talent's videos has been viewed.",
    },
    {
      title: 'Average Likes',
      description:
        'Average number of times each of the Talent’s videos has been liked.',
    },
    {
      title: 'Average Comments',
      description:
        'Average number of times each of the Talent’s videos has been commented on.',
    },
    {
      title: 'Average Shares',
      description:
        'Average number of times each of the Talent’s videos has been shared.',
    },
  ],
  snapchat: [
    {
      title: 'Story Average View Time',
      description:
        'Average time spent watching a Talent’s public stories before moving to the next snap (in milliseconds).',
    },
    {
      title: 'Saved Story Average View Time',
      description:
        'Average time spent watching a Talent’s saved stories (permanent stories on their profile) in milliseconds.',
    },
    {
      title: 'Spotlight Average View Time',
      description:
        'Average time spent watching a Talent’s spotlight videos before swiping away (in milliseconds).',
    },
  ],
};

const infoMetricsTitle = {
  instagram:
    'Averages are calculated based on Instagram data from the last 30 days.',
  tiktok:
    'Averages are calculated based on TikTok data from the last 30 videos.',
  youtube: "Averages are pulled directly from YouTube's API.",
  snapchat:
    "Averages are pulled directly from Snapchat's API, and are based on the last 28 days.",
};

interface Props {
  platform: SupportedPlatforms;
}

export const MetricsInfoPopover: React.FC<Props> = ({
  platform = SOCIAL_PLATFORMS.instagram,
}) => {
  return (
    <Popover placement="auto-start">
      <PopoverTrigger>
        <TextLink>Learn more about metrics</TextLink>
      </PopoverTrigger>
      <PopoverContent borderColor="primary.100" padding={4}>
        <PopoverHeader border={0}>
          <Paragraph size="md" color="primary.700" fontWeight="medium">
            Metrics data
          </Paragraph>
        </PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody>
          <Flex gap={1} alignItems="center" marginBottom={1}>
            <AlertIcon boxSize={4} />
            <Paragraph size="xs" color="primary.400" fontWeight="medium">
              {infoMetricsTitle[platform]}
            </Paragraph>
          </Flex>
          <Flex flexDirection={'column'} gap={1}>
            {metricsInfo[platform].map((metric) => (
              <Box key={metric.title}>
                <Paragraph
                  size="sm"
                  color="primary.700"
                  sx={{ '& + p': { marginTop: 0 } }}
                  fontWeight="medium"
                >
                  {metric.title}
                </Paragraph>
                <Paragraph
                  size="sm"
                  color="primary.700"
                  sx={{ '& + p': { marginTop: 0 } }}
                >
                  {metric.description}
                </Paragraph>
              </Box>
            ))}
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
